/**
 * Page /cee/dossiers — liste maquette des dossiers TRA-EQ-131.
 * Header titre + sub-titre + 4 cards stats + filtres chips + tableau.
 *
 * Filtres synchronises avec URL (?filter=collecte ou ?status=...).
 * Recherche debounce 300ms.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as ceeApi from '../api/cee.js';
import StatsCards from './components/StatsCards.jsx';
import FilterChips, { CHIPS } from './components/FilterChips.jsx';
import DossierRow from './components/DossierRow.jsx';

export default function CeeDossiersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterKey = searchParams.get('filter') || 'all';
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [dossiers, setDossiers] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    collecte: 0,
    pretSigner: 0,
    termines: 0,
    alertes: 0,
    byMandataire: { FEH: 0, RE: 0, GMB: 0 },
    byStatus: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastSync, setLastSync] = useState(new Date());

  /** filtres effectifs combinant chip selectionnee + search. */
  const activeFilter = useMemo(() => {
    const chip = CHIPS.find((c) => c.key === filterKey) || CHIPS[0];
    return { ...chip.filter, search: search || undefined };
  }, [filterKey, search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, st] = await Promise.all([
        ceeApi.listDossiers(activeFilter),
        ceeApi.getStats(),
      ]);
      setDossiers(list);
      setStats(st);
      setLastSync(new Date());
    } catch (err) {
      setError(err.message || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  // Initial load + reload sur change de filtre/search debounce
  const searchTimer = useRef(null);
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(searchTimer.current);
  }, [load, search]);

  function handleChipSelect(key) {
    const next = new URLSearchParams(searchParams);
    if (key === 'all') {
      next.delete('filter');
    } else {
      next.set('filter', key);
    }
    setSearchParams(next);
  }

  function handleSearchChange(e) {
    const v = e.target.value;
    setSearch(v);
    const next = new URLSearchParams(searchParams);
    if (v) next.set('q', v); else next.delete('q');
    setSearchParams(next, { replace: true });
  }

  const minutesAgo = Math.max(0, Math.round((Date.now() - lastSync.getTime()) / 60000));

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-[#0f172a]">Dossiers TRA-EQ-131</h1>
          <p className="text-sm text-[#64748b] mt-1">
            Velos cargo · Delegataire LORIS ENR · {stats.total} clients · {dossiers.reduce((s, d) => s + (d.velosQuota || 0), 0)} velos
          </p>
        </div>
        <div className="text-xs text-[#94a3b8]">
          Mis a jour il y a {minutesAgo} min
        </div>
      </div>

      <StatsCards stats={stats} />

      {/* Recherche + filtres */}
      <div className="flex flex-col gap-3 mb-4">
        <FilterChips
          active={filterKey}
          counts={stats.byStatus}
          onSelect={handleChipSelect}
        />
        <input
          type="text"
          placeholder="Rechercher un client (nom ou SIREN)..."
          value={search}
          onChange={handleSearchChange}
          className="w-full md:w-96 px-4 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-10">
          <div className="w-7 h-7 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto" />
        </div>
      ) : dossiers.length === 0 ? (
        <div className="bg-white rounded-xl p-10 text-center text-[#64748b]">
          Aucun dossier ne correspond a ce filtre.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wide font-semibold text-[#64748b]">Client</th>
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wide font-semibold text-[#64748b]">Velos</th>
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wide font-semibold text-[#64748b]">Documents &amp; Signature</th>
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wide font-semibold text-[#64748b]">Statut</th>
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wide font-semibold text-[#64748b]">Action</th>
              </tr>
            </thead>
            <tbody>
              {dossiers.map((d) => <DossierRow key={d.id} dossier={d} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
